import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { ARMcontext } from '../../utils/context/context';
import { useDashboardLeaderboard } from '../../utils/hooks/useDashboardLeaderboard.hook';
import type {
  DashboardLeaderboardEntry,
  DashboardLeaderboardMetricId,
  DashboardLeaderboardSortMode,
} from '../../interfaces/dashboardLeaderboardActivity';
import { LEADERBOARD_METRIC_OPTIONS, LEADERBOARD_SORT_OPTIONS, rangeSubtitle } from './leaderboardConstants';
import {
  LeaderboardCard,
  LeaderboardInnerBlueCard,
  LeaderboardCardHeader,
  LeaderboardCardTitle,
  LeaderboardCardSubtitle,
  LeaderboardCardTitleRow,
  LeaderboardHeaderTrendIcon,
  LeaderboardControls,
  LeaderboardDropdown,
  LeaderboardDropdownTrigger,
  LeaderboardDropdownMenu,
  LeaderboardDropdownOption,
  LeaderboardList,
  LeaderboardCta,
  LeaderboardRow,
  LeaderboardRank,
  LeaderboardName,
  LeaderboardScore,
} from './sectionStyles';

/** Collapsed preview matches design: first 7 rows + CTA to expand. */
const LEADERBOARD_PREVIEW_COUNT = 7;
const LEADERBOARD_PAGE_SIZE = 20;

type DashboardLeaderboardProps = {
  rangePreset: string;
  startDate: Date | null;
  endDate: Date | null;
  playersGroup?: string;
  playerNameSearch?: string;
};

/**
 * Leaderboard card: metric + sort dropdowns, preview list (7 rows), expandable full list with infinite scroll
 * loading pages of {@link LEADERBOARD_PAGE_SIZE}. Uses {@link useDashboardLeaderboard} with filters from props +
 * selected team from context.
 *
 * @param rangePreset - Dashboard date preset or `custom` with `startDate`/`endDate`.
 * @param playerNameSearch - Optional player name filter (`search` query on API).
 */
export function DashboardLeaderboard({
  rangePreset,
  startDate,
  endDate,
  playersGroup,
  playerNameSearch,
}: DashboardLeaderboardProps) {
  const { selectedTeam } = useContext(ARMcontext);
  const teamId = (selectedTeam as any)?._id || (selectedTeam as any)?.id || null;

  const [metric, setMetric] = useState<DashboardLeaderboardMetricId>('armScore');
  const [sort, setSort] = useState<DashboardLeaderboardSortMode>('topScore');
  const [fullListOpen, setFullListOpen] = useState(false);
  const [fullPage, setFullPage] = useState(1);
  const [fullEntries, setFullEntries] = useState<DashboardLeaderboardEntry[]>([]);
  const [fullTotal, setFullTotal] = useState(0);
  const [metricOpen, setMetricOpen] = useState(false);
  const [sortOpen, setSortOpen] = useState(false);
  const metricDropdownRef = useRef<HTMLDivElement | null>(null);
  const sortDropdownRef = useRef<HTMLDivElement | null>(null);
  const listRef = useRef<HTMLUListElement | null>(null);

  const filters = useMemo(
    () => ({
      teamId,
      rangePreset,
      startDate: startDate ? startDate.toISOString() : null,
      endDate: endDate ? endDate.toISOString() : null,
      playersGroup: playersGroup ?? '',
      playerNameSearch: playerNameSearch ?? '',
    }),
    [teamId, rangePreset, startDate, endDate, playersGroup, playerNameSearch],
  );

  useEffect(() => {
    setFullListOpen(false);
    setFullPage(1);
    setFullEntries([]);
    setFullTotal(0);
  }, [metric, sort, teamId, rangePreset, startDate, endDate, playersGroup, playerNameSearch]);

  useEffect(() => {
    const onPointerDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (metricDropdownRef.current && !metricDropdownRef.current.contains(target)) {
        setMetricOpen(false);
      }
      if (sortDropdownRef.current && !sortDropdownRef.current.contains(target)) {
        setSortOpen(false);
      }
    };

    document.addEventListener('mousedown', onPointerDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
    };
  }, []);

  const {
    data: previewData,
    isLoading: isPreviewLoading,
    isError: isPreviewError,
  } = useDashboardLeaderboard({
    metric,
    sort,
    filters,
    limit: LEADERBOARD_PREVIEW_COUNT,
    page: 1,
    enabled: !fullListOpen,
  });

  const {
    data: fullPageData,
    isLoading: isFullLoading,
    isError: isFullError,
    isFetching: isFullFetching,
  } = useDashboardLeaderboard({
    metric,
    sort,
    filters,
    limit: LEADERBOARD_PAGE_SIZE,
    page: fullPage,
    enabled: fullListOpen,
  });

  useEffect(() => {
    if (!fullListOpen || !fullPageData) return;
    setFullEntries(prev => {
      if (fullPage === 1) return fullPageData.entries;
      const seen = new Set(prev.map(e => e.playerId));
      const next = fullPageData.entries.filter(e => !seen.has(e.playerId));
      return [...prev, ...next];
    });
    setFullTotal(fullPageData.total ?? fullPageData.entries.length);
  }, [fullListOpen, fullPage, fullPageData]);

  const hasMoreFullEntries = fullEntries.length < fullTotal;

  /** When expanded, requests the next leaderboard page as the user scrolls near the list bottom. */
  const handleFullListScroll = () => {
    if (!fullListOpen) return;
    const el = listRef.current;
    if (!el) return;
    if (isFullLoading || isFullFetching || !hasMoreFullEntries) return;
    const distanceToBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    if (distanceToBottom <= 40) {
      setFullPage(prev => prev + 1);
    }
  };

  return (
    <LeaderboardCard>
      <LeaderboardCardHeader>
        <LeaderboardCardTitleRow>
          <div>
            <LeaderboardCardTitle>Leaderboard</LeaderboardCardTitle>
            <LeaderboardCardSubtitle>Top performers ({rangeSubtitle(rangePreset)})</LeaderboardCardSubtitle>
          </div>
          <LeaderboardHeaderTrendIcon aria-hidden focusable="false" />
        </LeaderboardCardTitleRow>
      </LeaderboardCardHeader>
      <LeaderboardControls>
        <LeaderboardDropdown ref={metricDropdownRef}>
          <LeaderboardDropdownTrigger
            type="button"
            aria-label="Metric"
            aria-expanded={metricOpen}
            onClick={() => {
              setMetricOpen(prev => !prev);
              setSortOpen(false);
            }}
          >
            {LEADERBOARD_METRIC_OPTIONS.find(o => o.id === metric)?.label ?? 'Metric'}
          </LeaderboardDropdownTrigger>
          {metricOpen && (
            <LeaderboardDropdownMenu role="listbox" aria-label="Metric options">
              {LEADERBOARD_METRIC_OPTIONS.map(o => (
                <LeaderboardDropdownOption
                  key={o.id}
                  type="button"
                  $selected={o.id === metric}
                  onClick={() => {
                    setMetric(o.id);
                    setMetricOpen(false);
                  }}
                >
                  {o.label}
                </LeaderboardDropdownOption>
              ))}
            </LeaderboardDropdownMenu>
          )}
        </LeaderboardDropdown>

        <LeaderboardDropdown ref={sortDropdownRef}>
          <LeaderboardDropdownTrigger
            type="button"
            aria-label="Sort"
            aria-expanded={sortOpen}
            onClick={() => {
              setSortOpen(prev => !prev);
              setMetricOpen(false);
            }}
          >
            {LEADERBOARD_SORT_OPTIONS.find(o => o.id === sort)?.label ?? 'Sort'}
          </LeaderboardDropdownTrigger>
          {sortOpen && (
            <LeaderboardDropdownMenu role="listbox" aria-label="Sort options">
              {LEADERBOARD_SORT_OPTIONS.map(o => (
                <LeaderboardDropdownOption
                  key={o.id}
                  type="button"
                  $selected={o.id === sort}
                  onClick={() => {
                    setSort(o.id);
                    setSortOpen(false);
                  }}
                >
                  {o.label}
                </LeaderboardDropdownOption>
              ))}
            </LeaderboardDropdownMenu>
          )}
        </LeaderboardDropdown>
      </LeaderboardControls>
      {(() => {
        const entries = fullListOpen ? fullEntries : previewData?.entries ?? [];
        const total = fullListOpen ? fullTotal : previewData?.total ?? entries.length;
        const isLoading = fullListOpen ? isFullLoading : isPreviewLoading;
        const isError = fullListOpen ? isFullError : isPreviewError;
        const showPreview = !fullListOpen && total > LEADERBOARD_PREVIEW_COUNT;
        const visible = entries;
        return (
          <>
            <LeaderboardInnerBlueCard>
              <LeaderboardList
                ref={listRef}
                role="list"
                aria-label="Leaderboard"
                aria-busy={isLoading}
                $scrollable={fullListOpen}
                onScroll={handleFullListScroll}
              >
                {isError && (
                  <LeaderboardRow>Unable to load leaderboard.</LeaderboardRow>
                )}
                {isLoading && entries.length === 0 && <LeaderboardRow>Loading…</LeaderboardRow>}
                {visible.map(entry => (
                  <LeaderboardRow
                    key={entry.playerId}
                    $rank={entry.rank}
                    role="listitem"
                  >
                    <LeaderboardRank>{entry.rank}</LeaderboardRank>
                    <LeaderboardName title={entry.playerName}>{entry.playerName}</LeaderboardName>
                    <LeaderboardScore>{entry.metricValue == null ? '—' : entry.metricValue}</LeaderboardScore>
                  </LeaderboardRow>
                ))}
                {!isLoading && entries.length === 0 && !isError && (
                  <LeaderboardRow>No players for this team and range.</LeaderboardRow>
                )}
                {fullListOpen && isFullFetching && entries.length > 0 && hasMoreFullEntries && (
                  <LeaderboardRow>Loading more…</LeaderboardRow>
                )}
              </LeaderboardList>
            </LeaderboardInnerBlueCard>
            {(showPreview || fullListOpen) && (
              <LeaderboardCta
                type="button"
                onClick={() => {
                  setFullListOpen(prev => {
                    if (!prev) {
                      setFullPage(1);
                      // Keep preview rows visible immediately when expanding,
                      // then hydrate to 20+ rows in background.
                      const previewEntries = previewData?.entries ?? [];
                      setFullEntries(previewEntries);
                      setFullTotal(previewData?.total ?? previewEntries.length);
                    }
                    return !prev;
                  });
                }}
              >
                {fullListOpen ? 'Show less' : 'View full leaderboard'}
              </LeaderboardCta>
            )}
          </>
        );
      })()}
    </LeaderboardCard>
  );
}
